/**
 * The vocabulary for the one-pass sheen (§52) — a soft band of brand light
 * that crosses a heading once, as it arrives, and is then gone.
 *
 * Its own module because `Sheen.tsx` exports a component and a file that does
 * may not also export constants without breaking fast refresh; the same reason
 * `revealMotion.ts` sits beside `Reveal.tsx`.
 *
 * Two rules decide the whole recipe:
 *
 * 1. **The sheen is painted behind the text, never through it.** The obvious
 *    implementation is `background-clip: text` with a travelling gradient,
 *    and it is the wrong one: the glyphs then take their colour from the
 *    gradient, so the heading's contrast becomes a function of where the
 *    animation happens to be — and lands at whatever the gradient's dead
 *    stops are when the sweep ends, or when the animation never runs at all.
 *    Here the text keeps its own `color` at full strength and the sweep is a
 *    separate layer underneath it, so the worst case is a slightly tinted
 *    background behind fully-contrasting ink.
 * 2. **It ends where it started.** The pass fades in, crosses, and fades out,
 *    so the resting state of a swept heading is identical to an unswept one.
 *    Nothing is left on the page for a visitor who arrives after it has run.
 */

/** The name under which `Sheen` registers the pass; shared so both agree. */
export const SHEEN_ANIMATION = 'kbxSheenSweep';

/**
 * One crossing.
 *
 * `translate3d` only, so the layer stays on the compositor and the pass can
 * never trigger layout — the rule every other animation on this site follows.
 * ±130% rather than ±100%: the gradient's bright band sits in the middle of
 * the layer, so at exactly 100% it is still half on screen.
 *
 * Opacity is ramped inside the keyframes rather than being left at 1, which is
 * what lets the resting state be `opacity: 0` and still need no second rule to
 * clean up after the animation.
 */
export const sheenSweepKeyframes = {
  '0%': { opacity: 0, transform: 'translate3d(-130%, 0, 0)' },
  '18%': { opacity: 1 },
  '82%': { opacity: 1 },
  '100%': { opacity: 0, transform: 'translate3d(130%, 0, 0)' },
} as const;

/**
 * The light itself: the brand red, at its scheme-appropriate value, in a band
 * about a third of the layer wide with everything either side transparent.
 *
 * `primary-mainChannel` and not a hex, so dark mode gets dark mode's lighter
 * red (#E4717C) without this file knowing that it exists. 0.28 is the ceiling
 * that keeps the pass visible on both surfaces while leaving white-on-sweep
 * above 8:1 and ink-on-sweep above 14:1 — the sheen is decoration and is
 * never allowed to become the thing carrying the contrast.
 */
export const SHEEN_GRADIENT = [
  'linear-gradient(100deg,',
  'transparent 0%,',
  'rgba(var(--mui-palette-primary-mainChannel) / 0.04) 32%,',
  'rgba(var(--mui-palette-primary-mainChannel) / 0.28) 50%,',
  'rgba(var(--mui-palette-primary-mainChannel) / 0.04) 68%,',
  'transparent 100%)',
].join(' ');

/**
 * Long enough to read as light moving across a heading rather than as a
 * flicker, short enough that it is over before a reader finishes the line.
 * The delay lets the heading's own `Reveal` settle first — the sheen crossing
 * a heading that is still fading in reads as a rendering fault.
 */
export const SHEEN_DURATION_MS = 1500;
export const SHEEN_DELAY_MS = 260;

/** Ease-out: the light enters fast and leaves slowly, as a real highlight does. */
export const SHEEN_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';
